/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { eventUtil } from '@marriott/mi-ui-library';
import { useViewData } from '../store';
import { PropertyData, OpenHotelPanelOptions } from '../molecules/MIntegratedImagery/MIntegratedImagery.types';

/**
 * Returns a `components` map for `MarkdownPreview` that intercepts `hqv://` links
 * and opens HQV instead of navigating. Falls back to a standard `<a>` for all
 * other hrefs so normal markdown links remain functional.
 *
 * @param onOpenHotelPanel - Callback to open the HQV panel
 * @param messageViewData - Optional message-specific viewData. When provided, uses this
 *                          instead of the global store to ensure prices match the search
 *                          criteria that was active when the message was created.
 */
export function useHqvMarkdownComponents(
  onOpenHotelPanel?: (propertyData: PropertyData, propertyId: string, options?: OpenHotelPanelOptions) => void,
  messageViewData?: any
): Record<string, React.ComponentType<any>> {
  const globalViewData = useViewData();

  // Use message-specific viewData if provided, otherwise fall back to global store
  const viewData = messageViewData ?? globalViewData;

  return React.useMemo(
    () => ({
      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
        if (href?.startsWith('hqv://')) {
          const propertyId = href.replace('hqv://', '');

          if (onOpenHotelPanel) {
            // Prefer full PropertyData from NLS search results for richer HQV context.
            const edges: any[] =
              viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges ?? [];

            const edge = edges.find(
              (e: any) =>
                e?.node?.property?.id === propertyId ||
                e?.node?.property?.id?.toUpperCase() === propertyId.toUpperCase()
            );

            // Fall back to a minimal stub so the link is always clickable, even
            // when the property was referenced by the LLM outside of an NLS search
            // (e.g. in a comparison or detail response with no prior hotel search).
            const propertyData: PropertyData = edge?.node?.property ?? { id: propertyId };

            return (
              <button
                type="button"
                className="t-font-s hqv-property-link"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'inherit',
                  textAlign: 'left',
                }}
                onClick={() => onOpenHotelPanel(propertyData, propertyId, { messageViewData: viewData })}
                onMouseEnter={() => eventUtil?.dispatch('propCardOver', { id: propertyId })}
                onMouseLeave={() => eventUtil?.dispatch('propCardUnOver', null)}
              >
                {children}
              </button>
            );
          }

          // No panel handler provided — render as non-clickable text to prevent
          // browser from trying to navigate to the hqv:// protocol URL
          return <span className="t-font-s hqv-property-link-disabled">{children}</span>;
        }

        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
    }),
    [viewData, onOpenHotelPanel]
  );
}
